import React, { useState } from 'react';
import { User, Mail, Phone, MapPin, Calendar, GraduationCap, Users, Edit2, Camera, Award, BookOpen, Clock } from 'lucide-react';

const studentData = {
  personal: {
    firstName: 'John',
    lastName: 'Smith',
    email: 'john.smith@school.edu',
    phone: '+1 (555) 123-4567',
    dateOfBirth: 'March 15, 2010',
    gender: 'Male',
    bloodGroup: 'O+',
    address: '123 Education Street, Academic City, CA 90210',
    nationality: 'American',
  },
  academic: {
    studentId: 'STU-2025-0042',
    class: '10-A',
    section: 'A',
    rollNumber: '15',
    admissionDate: 'April 10, 2020',
    academicYear: '2025-2026',
    house: 'Blue House',
    classTeacher: 'Ms. Sarah Davis',
  },
  parent: {
    fatherName: 'Robert Smith',
    fatherOccupation: 'Software Engineer',
    fatherPhone: '+1 (555) 234-5678',
    fatherEmail: 'robert.smith@email.com',
    motherName: 'Emily Smith',
    motherOccupation: 'Doctor',
    motherPhone: '+1 (555) 345-6789',
    motherEmail: 'emily.smith@email.com',
  },
  achievements: [
    { title: 'Science Fair Winner', date: '2025', description: 'First place in Regional Science Fair' },
    { title: 'Math Olympiad', date: '2024', description: 'Silver Medal in State Mathematics Olympiad' },
    { title: 'Perfect Attendance', date: '2024', description: 'Awarded for 100% attendance in academic year' },
  ],
};

const Profile = () => {
  const [activeTab, setActiveTab] = useState('personal');

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-6 sm:p-8 text-white">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div className="relative">
            <div className="w-28 h-28 rounded-full bg-white/20 flex items-center justify-center text-4xl font-bold border-4 border-white/30">
              JS
            </div>
            <button className="absolute bottom-0 right-0 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors">
              <Camera size={18} className="text-primary-600" />
            </button>
          </div>
          <div className="text-center sm:text-left flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold">{studentData.personal.firstName} {studentData.personal.lastName}</h1>
            <p className="text-primary-100 mt-1">Student ID: {studentData.academic.studentId}</p>
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 mt-4">
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                <GraduationCap size={16} />
                <span className="text-sm">Class {studentData.academic.class}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                <BookOpen size={16} />
                <span className="text-sm">Roll No. {studentData.academic.rollNumber}</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-3 py-1.5 rounded-full">
                <Clock size={16} />
                <span className="text-sm">Since {studentData.academic.admissionDate}</span>
              </div>
            </div>
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-white text-primary-600 rounded-lg font-medium hover:bg-primary-50 transition-colors">
            <Edit2 size={18} />
            <span>Edit Profile</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-3xl font-bold text-primary-600">92%</p>
          <p className="text-sm text-gray-500 mt-1">Attendance</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-3xl font-bold text-green-600">A+</p>
          <p className="text-sm text-gray-500 mt-1">Grade</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-3xl font-bold text-purple-600">5</p>
          <p className="text-sm text-gray-500 mt-1">Class Rank</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
          <p className="text-3xl font-bold text-amber-600">3</p>
          <p className="text-sm text-gray-500 mt-1">Achievements</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
        {['personal', 'academic', 'parent', 'achievements'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-3 font-medium text-sm transition-colors border-b-2 -mb-px whitespace-nowrap ${
              activeTab === tab
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)} {tab === 'parent' ? 'Details' : tab === 'achievements' ? '' : 'Info'}
          </button>
        ))}
      </div>

      {/* Personal Information */}
      {activeTab === 'personal' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <User size={20} className="text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500">Full Name</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.personal.firstName} {studentData.personal.lastName}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Email Address</label>
                  <p className="font-medium text-gray-900 mt-1 flex items-center gap-2">
                    <Mail size={16} className="text-gray-400" />
                    {studentData.personal.email}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Phone Number</label>
                  <p className="font-medium text-gray-900 mt-1 flex items-center gap-2">
                    <Phone size={16} className="text-gray-400" />
                    {studentData.personal.phone}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Date of Birth</label>
                  <p className="font-medium text-gray-900 mt-1 flex items-center gap-2">
                    <Calendar size={16} className="text-gray-400" />
                    {studentData.personal.dateOfBirth}
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500">Gender</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.personal.gender}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Blood Group</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.personal.bloodGroup}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Nationality</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.personal.nationality}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Address</label>
                  <p className="font-medium text-gray-900 mt-1 flex items-start gap-2">
                    <MapPin size={16} className="text-gray-400 mt-0.5" />
                    {studentData.personal.address}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Academic Information */}
      {activeTab === 'academic' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <GraduationCap size={20} className="text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Academic Information</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500">Student ID</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.academic.studentId}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Class & Section</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.academic.class}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Roll Number</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.academic.rollNumber}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">House</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.academic.house}</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-500">Academic Year</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.academic.academicYear}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Admission Date</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.academic.admissionDate}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Class Teacher</label>
                  <p className="font-medium text-gray-900 mt-1">{studentData.academic.classTeacher}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Parent Information */}
      {activeTab === 'parent' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Users size={20} className="text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Father's Information</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-500">Name</label>
                    <p className="font-medium text-gray-900 mt-1">{studentData.parent.fatherName}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Occupation</label>
                    <p className="font-medium text-gray-900 mt-1">{studentData.parent.fatherOccupation}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-500">Phone</label>
                    <p className="font-medium text-gray-900 mt-1">{studentData.parent.fatherPhone}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Email</label>
                    <p className="font-medium text-gray-900 mt-1">{studentData.parent.fatherEmail}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <Users size={20} className="text-pink-600" />
                <h2 className="text-lg font-semibold text-gray-900">Mother's Information</h2>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-500">Name</label>
                    <p className="font-medium text-gray-900 mt-1">{studentData.parent.motherName}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Occupation</label>
                    <p className="font-medium text-gray-900 mt-1">{studentData.parent.motherOccupation}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-500">Phone</label>
                    <p className="font-medium text-gray-900 mt-1">{studentData.parent.motherPhone}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Email</label>
                    <p className="font-medium text-gray-900 mt-1">{studentData.parent.motherEmail}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Achievements */}
      {activeTab === 'achievements' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <Award size={20} className="text-amber-600" />
              <h2 className="text-lg font-semibold text-gray-900">Achievements & Awards</h2>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {studentData.achievements.map((achievement, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award size={24} className="text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{achievement.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{achievement.description}</p>
                    <p className="text-xs text-amber-600 mt-2">{achievement.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
